import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useIdeaCategories,
  useIdeaList,
  useIdeaPriorities,
  useIdeaStatuses,
  useIdeaTags,
  useRemoveIdeaVote,
  useToggleIdeaPin,
  useVoteIdea,
} from "../hooks/useIdeas";
import { IdeaCard } from "./IdeaCard";
import { NewIdeaDrawer, type IdeaPrefill } from "./NewIdeaDrawer";
import type { IdeaListItem } from "../types";

type Filter = {
  companyId?: string;
  productId?: string;
  projectId?: string;
  meetingId?: string;
  taskId?: string;
};

type Props = {
  filter: Filter;
  prefill: IdeaPrefill;
};

export function IdeasTab({ filter, prefill }: Props) {
  const navigate = useNavigate();
  const [newOpen, setNewOpen] = useState(false);

  const enabled = Boolean(
    filter.companyId ||
      filter.productId ||
      filter.projectId ||
      filter.meetingId ||
      filter.taskId,
  );

  const { data, isLoading } = useIdeaList(
    enabled
      ? {
          page: 1,
          limit: 50,
          ...filter,
        }
      : { page: 1, limit: 0 },
  );

  const togglePin = useToggleIdeaPin();
  const categories = useIdeaCategories().data ?? [];
  const priorities = useIdeaPriorities().data ?? [];
  const statuses = useIdeaStatuses().data ?? [];
  const tagOptions = useIdeaTags().data ?? [];

  const tagLabels = useMemo(
    () => Object.fromEntries(tagOptions.map((t) => [t.value, t.label])),
    [tagOptions],
  );
  const categoryLabels = useMemo(
    () => Object.fromEntries(categories.map((t) => [t.value, t.label])),
    [categories],
  );
  const statusLabels = useMemo(
    () => Object.fromEntries(statuses.map((t) => [t.value, t.label])),
    [statuses],
  );
  const priorityLabels = useMemo(
    () => Object.fromEntries(priorities.map((t) => [t.value, t.label])),
    [priorities],
  );

  const ideas: IdeaListItem[] = enabled ? data?.data ?? [] : [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {ideas.length} idea{ideas.length === 1 ? "" : "s"}
        </div>
        <Button size="sm" onClick={() => setNewOpen(true)}>
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          New Idea
        </Button>
      </div>

      {isLoading && enabled ? (
        <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading ideas…
        </div>
      ) : ideas.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No ideas linked yet
        </div>
      ) : (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
        >
          {ideas.map((idea) => (
            <IdeaCardWithVote
              key={idea.id}
              idea={idea}
              onClick={() => navigate(`/ideas/${idea.id}`)}
              onTogglePin={() =>
                togglePin.mutate({ id: idea.id, isPinned: !idea.isPinned })
              }
              tagLabels={tagLabels}
              categoryLabels={categoryLabels}
              statusLabels={statusLabels}
              priorityLabels={priorityLabels}
            />
          ))}
        </div>
      )}

      <NewIdeaDrawer
        isOpen={newOpen}
        onClose={() => setNewOpen(false)}
        prefill={prefill}
      />
    </div>
  );
}

function IdeaCardWithVote(props: {
  idea: IdeaListItem;
  onClick: () => void;
  onTogglePin: () => void;
  tagLabels: Record<string, string>;
  categoryLabels: Record<string, string>;
  statusLabels: Record<string, string>;
  priorityLabels: Record<string, string>;
}) {
  const vote = useVoteIdea(props.idea.id);
  const removeVote = useRemoveIdeaVote(props.idea.id);

  async function handle(type: "up" | "down") {
    try {
      if (props.idea.currentUserVote === type) {
        await removeVote.mutateAsync();
      } else {
        await vote.mutateAsync(type);
      }
    } catch {
      // swallow
    }
  }

  return (
    <IdeaCard
      idea={props.idea}
      onClick={props.onClick}
      onTogglePin={props.onTogglePin}
      onVote={handle}
      tagLabels={props.tagLabels}
      categoryLabels={props.categoryLabels}
      statusLabels={props.statusLabels}
      priorityLabels={props.priorityLabels}
    />
  );
}
